import { ChatMessage, type ChatMessageProps } from '~/components/chat/ChatMessage'
import { SaveAsBookmarkButton } from '~/components/chat/BookmarksPopover'
import { MessageUploadCardsContainer } from '~/components/chat/MessageUploadCardsContainer'
import { InitialsAvatar } from '~/components/common/InitialsAvatar'
import { Typography } from '~/components/common/Typography'
import { bridge } from '~/services/bridge'
import { cm } from '~/utils'
import { formatMessageTime } from '~/utils/formatMessageTime'

export type UserMessageProps = ChatMessageProps

/**
 * Renders a user's chat message bubble.
 *
 * User content is rendered as PLAIN TEXT (whitespace preserved), never markdown
 * — matching ChatGPT/Claude, which only render markdown on assistant responses.
 * This is deliberate: feeding user input through react-markdown let pasted
 * content be mis-parsed (e.g. a line of space-separated `+`/`*` operator tokens
 * became ~1000 nested bullet lists, overflowing the renderer and taking down the
 * whole conversation — prod conversation 42719). Plain text removes that entire
 * crash class and shows the message exactly as typed.
 *
 * This branch used to live inside ChatMessage; it owns user-specific rendering
 * now that ChatDisplay routes text+user messages here.
 */
export function UserMessage(props: UserMessageProps) {
  const { message, isSplitPaneLayout = false } = props

  // ChatDisplay only routes text+user messages here; anything else falls back
  // to the shared renderer defensively.
  if (message.type !== 'text' || message.role !== 'user') {
    return <ChatMessage {...props} />
  }

  const isCompact = bridge.isInPlugin()
  const { contextUpload: contextUploads, userName, createdTime, content } = message
  const formattedTime = formatMessageTime(createdTime)

  return (
    <div
      className={cm('flex w-full min-w-0 flex-col gap-2', 'items-end')}
      data-testid='user-message-stack'
    >
      {userName && (
        <div className={cm('flex items-center gap-2', isCompact ? 'max-w-full' : 'max-w-[80%]')}>
          <InitialsAvatar name={userName} size='xs' />
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            fontWeight='semibold'
            className='text-tokens-neutral-text-secondary'
          >
            {userName}
          </Typography>
          {formattedTime && (
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='normal'
              className='text-tokens-neutral-text-tertiary'
            >
              {formattedTime}
            </Typography>
          )}
          <SaveAsBookmarkButton prompt={content} />
        </div>
      )}
      {/* Display upload cards above user message if present */}
      {contextUploads && contextUploads.length > 0 && (
        <MessageUploadCardsContainer uploads={contextUploads} />
      )}
      <div
        data-testid='user-message-bubble'
        className={cm(
          'min-w-0 break-words',
          isSplitPaneLayout
            ? 'flex items-center justify-end gap-3 rounded-[24px] bg-tokens-neutral-fill-fill-4 p-4'
            : 'rounded-3xl bg-tokens-neutral-fill-fill-4',
          isCompact ? 'p-3 max-w-full' : 'max-w-[80%]',
          !isSplitPaneLayout && 'p-4'
        )}
      >
        <Typography
          fontFamily='geist'
          fontSize='bodyMdMedium'
          fontWeight='medium'
          className={cm(
            'text-tokens-neutral-text-primary w-full whitespace-pre-wrap break-words',
            isSplitPaneLayout && 'text-right'
          )}
        >
          {content}
        </Typography>
      </div>
    </div>
  )
}
